import { lazy, Suspense, useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import type { FormField } from '../features/surveys/types';
import {
  fetchFormTemplateByCode,
  type FormTemplateDetail,
  type FormTemplateFieldGroup,
} from '../features/formTemplates/formTemplatesApi';
import { ExtendedField } from '../features/formTemplates/ExtendedFieldRenderer';
import { useFormulaEngine } from '../features/formTemplates/useFormulaEngine';
import { validateSurveyField } from '../features/surveys/surveyFieldValidation';

const SpreadsheetTemplatePage = lazy(() =>
  import('./SpreadsheetTemplatePage').then((m) => ({ default: m.SpreadsheetTemplatePage })),
);

type FormValues = Record<string, unknown>;

function normalizeField(field: FormField): FormField {
  return { ...field, required: field.required ?? false };
}

function formatWeight(group: FormTemplateFieldGroup): string {
  return typeof group.weight === 'number' && group.weight > 0 ? ` · ${group.weight}%` : '';
}

export function FormTemplatePage() {
  const { code } = useParams<{ code: string }>();
  const [template, setTemplate] = useState<FormTemplateDetail | null>(null);
  const [values, setValues] = useState<FormValues>({});
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    async function loadTemplate() {
      if (!code) {
        setError('Código de plantilla requerido.');
        setLoading(false);
        return;
      }
      try {
        setLoading(true);
        const data = await fetchFormTemplateByCode(code);
        if (!cancelled) setTemplate(data);
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Error al cargar la plantilla.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    void loadTemplate();
    return () => {
      cancelled = true;
    };
  }, [code]);

  const fields = useMemo(
    () => (template?.currentVersion?.structure?.fields ?? []).map(normalizeField),
    [template],
  );
  const groups = useMemo(
    () => [...(template?.currentVersion?.structure?.fieldGroups ?? [])].sort((a, b) => (a.order ?? 0) - (b.order ?? 0)),
    [template],
  );
  const engine = useFormulaEngine({ fields, values });

  const validateField = (fieldId: string, value: unknown) => {
    const field = fields.find((f) => f.id === fieldId);
    if (!field) return;
    const message = validateSurveyField(field, value);
    setFieldErrors((prev) => {
      const next = { ...prev };
      if (message) next[fieldId] = message;
      else delete next[fieldId];
      return next;
    });
  };

  const setFieldValue = (fieldId: string, value: unknown) => {
    setValues((prev) => ({ ...prev, [fieldId]: value }));
    const field = fields.find((f) => f.id === fieldId);
    if (fieldErrors[fieldId] || field?.type === 'number' || field?.type === 'date') {
      validateField(fieldId, value);
    }
  };

  const onFieldBlur = (fieldId: string) => {
    validateField(fieldId, values[fieldId]);
  };

  if (loading) {
    return <p className='text-sm text-muted-foreground'>Cargando formulario…</p>;
  }

  if (error || !template) {
    return (
      <div className='rounded-md border border-destructive/30 bg-destructive/10 p-4 text-sm text-destructive'>
        {error || 'Plantilla no encontrada.'}
      </div>
    );
  }

  if (template.displayMode === 'spreadsheet') {
    return (
      <Suspense fallback={<p className='text-sm text-muted-foreground'>Cargando planilla…</p>}>
        <SpreadsheetTemplatePage template={template} />
      </Suspense>
    );
  }

  return (
    <div className='max-w-5xl space-y-6'>
      <header className='space-y-2'>
        <div className='flex items-start justify-between gap-2'>
          <div className='text-xs font-medium uppercase tracking-wide text-muted-foreground'>
            {template.taxonomy?.name ?? 'Formulario'} · {template.code}
          </div>
          <Link
            to={`/f/${encodeURIComponent(template.code)}/captura`}
            className='text-sm text-primary hover:underline'
          >
            Captura y seguimiento
          </Link>
        </div>
        <h1 className='text-2xl font-semibold text-foreground'>{template.name}</h1>
        {template.description && (
          <p className='max-w-4xl text-sm text-muted-foreground'>{template.description}</p>
        )}
      </header>

      <div className='rounded-lg border border-border bg-card p-5 shadow-sm'>
        {groups.length > 0 ? (
          <div className='space-y-6'>
            {groups.map((group) => (
              <section key={group.id} className='space-y-4'>
                <h2 className='border-b border-border pb-2 text-lg font-semibold text-foreground'>
                  {group.name}
                  <span className='text-sm font-normal text-muted-foreground'>{formatWeight(group)}</span>
                </h2>
                <div className='grid grid-cols-1 gap-4 md:grid-cols-2'>
                  {fields
                    .filter((field) => field.groupId === group.id)
                    .filter((field) => engine.visibleSet.has(field.id))
                    .map((field) => (
                      <TemplateField
                        key={field.id}
                        field={field}
                        value={values[field.id]}
                        required={engine.requiredSet.has(field.id)}
                        error={fieldErrors[field.id]}
                        engine={engine}
                        onChange={(value) => setFieldValue(field.id, value)}
                        onBlur={() => onFieldBlur(field.id)}
                      />
                    ))}
                </div>
              </section>
            ))}
          </div>
        ) : (
          <div className='grid grid-cols-1 gap-4 md:grid-cols-2'>
            {fields
              .filter((field) => engine.visibleSet.has(field.id))
              .map((field) => (
                <TemplateField
                  key={field.id}
                  field={field}
                  value={values[field.id]}
                  required={engine.requiredSet.has(field.id)}
                  error={fieldErrors[field.id]}
                  engine={engine}
                  onChange={(value) => setFieldValue(field.id, value)}
                  onBlur={() => onFieldBlur(field.id)}
                />
              ))}
          </div>
        )}
      </div>
    </div>
  );
}

interface TemplateFieldProps {
  field: FormField;
  value: unknown;
  required: boolean;
  error?: string;
  engine: ReturnType<typeof useFormulaEngine>;
  onChange: (value: unknown) => void;
  onBlur?: () => void;
}

function fieldInputClass(hasError: boolean): string {
  const border = hasError ? 'border-destructive' : 'border-input';
  return `w-full rounded-md border ${border} bg-background px-3 py-2 text-sm`;
}

function FieldErrorMessage({ error }: { error?: string }) {
  if (!error) return null;
  return <p className="text-sm text-destructive">{error}</p>;
}

function TemplateField({ field, value, required, error, engine, onChange, onBlur }: TemplateFieldProps) {
  if (field.type === 'calculated' || field.type === 'lookup' || field.type === 'reference') {
    return <ExtendedField field={field} value={value} onChange={onChange} engine={engine} />;
  }

  const label = (
    <label htmlFor={field.id} className='block text-sm font-medium text-foreground'>
      {field.label}
      {required && <span className='ml-1 text-destructive'>*</span>}
    </label>
  );

  if (field.type === 'textarea') {
    return (
      <div className='space-y-1 md:col-span-2'>
        {label}
        <textarea
          id={field.id}
          className={`min-h-24 ${fieldInputClass(!!error)}`}
          placeholder={field.placeholder}
          value={String(value ?? '')}
          onChange={(e) => onChange(e.target.value)}
          onBlur={onBlur}
          required={required}
        />
        <FieldErrorMessage error={error} />
      </div>
    );
  }

  if (field.type === 'radio') {
    return (
      <fieldset className='space-y-2'>
        <legend className='text-sm font-medium text-foreground'>
          {field.label}
          {required && <span className='ml-1 text-destructive'>*</span>}
        </legend>
        <div className='space-y-1'>
          {(field.options ?? []).map((option) => (
            <label key={option} className='flex items-center gap-2 text-sm text-foreground'>
              <input
                type='radio'
                name={field.id}
                value={option}
                checked={value === option}
                onChange={() => onChange(option)}
                onBlur={onBlur}
                required={required}
              />
              <span>{option}</span>
            </label>
          ))}
        </div>
        <FieldErrorMessage error={error} />
      </fieldset>
    );
  }

  if (field.type === 'checkbox-multiple') {
    const selected = Array.isArray(value) ? value : [];
    return (
      <fieldset className='space-y-2 md:col-span-2'>
        <legend className='text-sm font-medium text-foreground'>
          {field.label}
          {required && <span className='ml-1 text-destructive'>*</span>}
        </legend>
        <div className='grid grid-cols-1 gap-1 md:grid-cols-2'>
          {(field.options ?? []).map((option) => (
            <label key={option} className='flex items-center gap-2 text-sm text-foreground'>
              <input
                type='checkbox'
                checked={selected.includes(option)}
                onChange={(e) => {
                  onChange(e.target.checked ? [...selected, option] : selected.filter((item) => item !== option));
                }}
                onBlur={onBlur}
              />
              <span>{option}</span>
            </label>
          ))}
        </div>
        <FieldErrorMessage error={error} />
      </fieldset>
    );
  }

  if (field.type === 'select') {
    return (
      <div className='space-y-1'>
        {label}
        <select
          id={field.id}
          className={fieldInputClass(!!error)}
          value={String(value ?? '')}
          onChange={(e) => onChange(e.target.value)}
          onBlur={onBlur}
          required={required}
        >
          <option value=''>— Seleccionar —</option>
          {(field.options ?? []).map((option) => (
            <option key={option} value={option}>{option}</option>
          ))}
        </select>
        <FieldErrorMessage error={error} />
      </div>
    );
  }

  if (field.type === 'checkbox') {
    return (
      <div className="space-y-1">
        <label className='flex items-center gap-2 text-sm font-medium text-foreground'>
          <input
            type='checkbox'
            checked={Boolean(value)}
            onChange={(e) => onChange(e.target.checked)}
            onBlur={onBlur}
          />
          <span>{field.label}</span>
        </label>
        <FieldErrorMessage error={error} />
      </div>
    );
  }

  return (
    <div className='space-y-1'>
      {label}
      <input
        id={field.id}
        type={field.type === 'number' ? 'number' : field.type === 'date' ? 'date' : 'text'}
        className={fieldInputClass(!!error)}
        placeholder={field.placeholder}
        value={String(value ?? '')}
        min={field.validation?.min}
        max={field.validation?.max}
        pattern={field.validation?.pattern}
        onChange={(e) => {
          const raw = e.target.value;
          if (field.type === 'number') {
            if (raw === '') {
              onChange('');
              return;
            }
            const parsed = Number(raw);
            onChange(Number.isNaN(parsed) ? raw : parsed);
            return;
          }
          onChange(raw);
        }}
        onBlur={onBlur}
        required={required}
      />
      <FieldErrorMessage error={error} />
    </div>
  );
}
